import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatNumber, hasPermission } from '@blimo/shared';
import { Badge, Button, DataTable, ErrorState, PageHeader, SearchInput } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { SourceBadge } from '../../components/data.jsx';
import { useListState } from '../../lib/list-search.js';
import { CreateClientSheet } from './create-sheet.jsx';

const ARCHIVE_OPTIONS = [{ value: '1', label: 'Inkl. archivierter' }];

const count = (/** @type {number|undefined} */ n) => <span className="numeric">{formatNumber(n ?? 0, { maximumFractionDigits: 0 })}</span>;

/** Mandanten & Freigaben (F-013): Mandanten-Organisationen mit Kennzahlen; Anlegen nur mit `mandates.manage`. */
export function MandatesListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const [createOpen, setCreateOpen] = useState(false);
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter: { q: search.q, archived: search.archived } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.mandates(api, params));
  const columns = useMemo(() => clientColumns(), []);
  const canManage = hasPermission(me, 'mandates.manage');

  return (
    <>
      <PageHeader
        title="Mandanten & Freigaben"
        description="Welche Einheiten ein Mandant im Kundenportal sieht, welche Kostenkonten freigegeben sind und wer zum Mandanten gehört."
        actions={
          canManage && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> Mandant anlegen
            </Button>
          )
        }
      />
      <FilterBar>
        <SearchInput label="Mandanten durchsuchen" placeholder="Name …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Archiv" value={search.archived} onChange={(v) => list.setFilter('archived', v)} options={ARCHIVE_OPTIONS} allLabel="Nur aktive" />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Mandanten"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Mandanten gefunden"
          emptyDescription={canManage ? 'Legen Sie einen Mandanten an oder kennzeichnen Sie eine bestehende Organisation.' : undefined}
        />
      )}
      {canManage && (
        <CreateClientSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(client) => navigate({ to: '/finanzen/mandanten/$clientId', params: { clientId: String(client.id) } })}
        />
      )}
    </>
  );
}

function clientColumns() {
  return [
    {
      id: 'name',
      header: 'Mandant',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <Link to="/finanzen/mandanten/$clientId" params={{ clientId: String(row.original.id) }} className="font-medium text-primary hover:underline">
            {row.original.name || `#${row.original.id}`}
          </Link>
          {row.original.archived_at && <Badge variant="neutral">Archiviert</Badge>}
        </span>
      ),
    },
    { id: 'units', header: 'Einheiten', enableSorting: false, meta: { numeric: true }, cell: ({ row }) => count(row.original.counts?.units) },
    { id: 'properties', header: 'Liegenschaften', enableSorting: false, meta: { numeric: true }, cell: ({ row }) => count(row.original.counts?.properties) },
    {
      id: 'cost_accounts',
      header: 'Kostenkonten',
      enableSorting: false,
      meta: { numeric: true },
      cell: ({ row }) => count(row.original.counts?.cost_accounts),
    },
    { id: 'members', header: 'Mitglieder', enableSorting: false, meta: { numeric: true }, cell: ({ row }) => count(row.original.counts?.members) },
    { id: 'source', header: 'Quelle', enableSorting: false, cell: ({ row }) => <SourceBadge source={row.original.source} /> },
  ];
}
